<template>
  <el-dialog custom-class="payment-dialog" :title="$t('message.home.pendingPayment')" :visible.sync="dialogVisible">
    <div class="wrap" v-loading="payLoading">
      <div class="title">{{ $t('message.home.pendingPayment') }}</div>
      <EsPayMethods
        v-model="paymentMethod"
        :showTitle="false"
        :availableBalance="availableBalance"
        :amountPayable="amountPayable"
        :useDepositBalance.sync="useDepositBalance"
        :balanceInput.sync="balanceInput"
        @deposit-change="onDepositChange"
      />
      <div
        v-if="paymentMethod === 'stripePayment' && stripeStarted"
        class="stripe-container"
        style="margin: 16px 0;"
      >
        <div id="order-stripe-payment-element"></div>
        <el-button
          v-if="stripeReady"
          type="primary"
          :loading="stripeLoading"
          style="width: 100%; margin-top: 12px;"
          @click="handleStripePay"
        >
          {{ $t('message.home.pay') }}
        </el-button>
      </div>
      <div v-else class="action">
        <el-button class="pay-btn" :loading="payLoading" @click="pay">{{ $t('message.home.pay') }}</el-button>
      </div>
    </div>

    <el-dialog
      :visible.sync="paypalDialogVisible"
      class="es-dialog"
      :append-to-body="true"
      width="500px"
      :close-on-click-modal="false"
      @close="onPaypalDialogClose"
    >
      <div slot="title" class="dialog-title">
        <span>{{ $t('message.home.payPal') }}</span>
      </div>
      <div class="pay-modal-content" style="padding: 20px 30px;">
        <div id="order-paypal-button-container"></div>
        <div id="order-paypal-card-button-container" style="margin-top: 12px;"></div>
      </div>
      <span slot="footer"></span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import EsPayMethods from '@/page/settlementPage/payMethod';
import { payment, deposit_pay } from '@/api/order/order.js';
import {
  index as paymentIndex,
  load as paymentLoad,
  pay as paymentPay,
  postPay,
  testPostPay,
  is_pay_success,
} from '@/api/order/payment.js';

export default {
  name: 'order-payment-dialog',
  components: { EsPayMethods },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    currentOrderInfo: {
      type: Object,
      default: () => ({}),
    },
    refresh: {
      type: Function,
    },
  },
  data() {
    return {
      dialogVisible: this.value || false,
      paymentMethod: 'bizSystem',
      payLoading: false,
      orderSns: [],
      orders: [],
      totalPrice: 0,
      amountPayable: 0,
      availableBalance: 0,
      useDepositBalance: false,
      balanceInput: 0,
      paymentItemListForm: {
        paymentItemList: [],
      },
      defaultPayment: null,
      paymentLog: null,
      paypalSDKLoaded: false,
      paypalSDKCurrency: '',
      paypalDialogVisible: false,
      stripeInstance: null,
      stripeElements: null,
      stripeReady: false,
      stripeLoading: false,
      stripeStarted: false,
      stripePaymentLogSn: null,
    };
  },
  computed: {
    ...mapGetters('user', ['userBalance']),
  },
  watch: {
    value(val) {
      this.dialogVisible = val;
    },
    dialogVisible(val) {
      if (val !== this.value) {
        this.$emit('input', val);
      }
      if (val) {
        this.initPaymentData();
      } else {
        this.onPaypalDialogClose();
        this.resetStripeState();
      }
    },
    paymentMethod(val) {
      if (val !== 'stripePayment') {
        this.resetStripeState();
      }
    },
  },
  methods: {
    ...mapActions({
      requestGetUserBalanceList: 'user/requestGetUserBalanceList',
    }),
    parseAmount(val) {
      const n = Number(val);
      return Number.isFinite(n) ? n : 0;
    },
    getOrderSn() {
      return this.currentOrderInfo.sn || this.currentOrderInfo.orderSn || '';
    },
    onDepositChange() {},
    async initPaymentData() {
      this.paymentMethod = 'bizSystem';
      this.useDepositBalance = false;
      this.balanceInput = 0;
      this.resetStripeState();
      try {
        await this.requestGetUserBalanceList();
        await this.preparePaymentContext();
        if (this.parseAmount(this.availableBalance) <= 0) {
          this.availableBalance = this.parseAmount(this.userBalance);
        }
      } catch (error) {
        console.error('initPaymentData error', error);
        this.availableBalance = this.parseAmount(this.userBalance);
      }
    },
    buildPaymentItems() {
      this.paymentItemListForm.paymentItemList = (this.orders || []).map((order) => ({
        type: 'ORDER_PAYMENT',
        amount: order.amountPayable != null ? order.amountPayable : order.amount,
        orderSn: order.sn,
      }));
    },
    async preparePaymentContext() {
      const orderSn = this.getOrderSn();
      if (!orderSn) {
        throw new Error('missing orderSn');
      }
      const response = await payment([orderSn]);
      const data = response.data.data || {};
      this.orders = data.orders || [];
      this.orderSns = data.orderSns || [orderSn];
      this.totalPrice = data.amount;
      if (data.availableBalance != null) {
        this.availableBalance = data.availableBalance;
      } else if (this.currentOrderInfo.availableBalance != null) {
        this.availableBalance = this.currentOrderInfo.availableBalance;
      }
      const payable = (this.orders || []).reduce(
        (sum, order) => sum + this.parseAmount(order.amountPayable),
        0
      );
      this.amountPayable = payable > 0 ? payable : this.parseAmount(data.amount);
      this.defaultPayment = data.defaultPayment || null;
      this.buildPaymentItems();
      return data;
    },
    isOrderFullyPaid() {
      const payable = (this.orders || []).reduce(
        (sum, order) => sum + this.parseAmount(order.amountPayable),
        0
      );
      return payable <= 0;
    },
    async applyDepositIfNeeded() {
      const amount = this.parseAmount(this.balanceInput);
      if (!this.useDepositBalance || amount <= 0) {
        return false;
      }
      const orderSns = Array.isArray(this.orderSns) ? this.orderSns : [this.getOrderSn()];
      await deposit_pay({
        orderSns,
        balance: amount,
      });
      await this.preparePaymentContext();
      return this.isOrderFullyPaid();
    },
    async pay() {
      this.payLoading = true;
      try {
        if (this.useDepositBalance && this.parseAmount(this.balanceInput) > 0) {
          const fullyPaid = await this.applyDepositIfNeeded();
          if (fullyPaid) {
            this.$emit('closePay');
            this.refresh && this.refresh();
            this.dialogVisible = false;
            this.$router.replace('/paySuccess');
            return;
          }
        }
        if (this.paymentMethod === 'bizSystem') {
          await this.startBizSystemPayment();
          return;
        }
        if (this.paymentMethod === 'paypalPayment') {
          await this.startPaypalPayment();
          return;
        }
        if (this.paymentMethod === 'stripePayment') {
          await this.startStripePayment();
        }
      } catch (error) {
        console.error('pay error', error);
      } finally {
        this.payLoading = false;
      }
    },
    async startBizSystemPayment() {
      await this.preparePaymentContext();
      const paymentId = this.defaultPayment && this.defaultPayment.id;
      if (!paymentId) {
        this.$message({ message: 'Payment method not available', type: 'error' });
        throw new Error('missing defaultPayment');
      }
      const response = await paymentLoad(this.paymentItemListForm, {
        paymentId,
        rePayUrl: 'test',
      });
      const payload = (response.data && response.data.data) || {};
      this.paymentLog = payload.paymentLog;
      if (!this.paymentLog || !this.paymentLog.sn) {
        this.$message({ message: 'Failed to create payment log', type: 'error' });
        throw new Error('missing paymentLog');
      }
      await testPostPay({ extra: 'Order pay' }, this.paymentLog.sn);
      const verifyRes = await is_pay_success({ paymentLogSn: this.paymentLog.sn });
      const confirmed = verifyRes && verifyRes.data && verifyRes.data.data && verifyRes.data.data.isPaySuccess;
      if (!confirmed) {
        this.$message({ message: this.$t('message.home.paymentFailed') || 'Payment failed', type: 'error' });
        throw new Error('payment not confirmed');
      }
      this.$emit('closePay');
      this.refresh && this.refresh();
      this.dialogVisible = false;
      this.$router.replace({ path: '/paySuccess', query: { paymentLogSn: this.paymentLog.sn } });
    },
    loadPaypalSDK(clientId, currency) {
      return new Promise((resolve, reject) => {
        const cur = currency || 'USD';
        if (this.paypalSDKLoaded && this.paypalSDKCurrency === cur) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://www.paypal.com/sdk/js?client-id=' + encodeURIComponent(clientId) + '&currency=' + encodeURIComponent(cur);
        script.onload = () => {
          this.paypalSDKLoaded = true;
          this.paypalSDKCurrency = cur;
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async startPaypalPayment() {
      try {
        await this.preparePaymentContext();
        const response = await paymentIndex(this.paymentItemListForm, {
          paymentId: 'paypalPayment',
          rePayUrl: 'payment',
        });
        this.paymentLog = response.data.data.paymentLog;
        const payRes = await paymentPay({
          extra: 'Order pay',
          paymentLogSn: this.paymentLog.sn,
        });
        const resultAmount = payRes.data.data.resultAmount;
        const resultOrderId = payRes.data.data.resultOrderId;
        const clientId = payRes.data.data.clientId;
        const currency = payRes.data.data.currency || 'USD';
        const paymentLogSn = this.paymentLog.sn;
        const self = this;

        this.paypalDialogVisible = true;
        await this.loadPaypalSDK(clientId, currency);
        this.$nextTick(() => {
          const ppContainer = document.getElementById('order-paypal-button-container');
          const cardContainer = document.getElementById('order-paypal-card-button-container');
          if (ppContainer) ppContainer.innerHTML = '';
          if (cardContainer) cardContainer.innerHTML = '';

          const buttonConfig = {
            createOrder(data, actions) {
              return actions.order.create({
                purchase_units: [{
                  reference_id: resultOrderId,
                  amount: { value: String(resultAmount), currency_code: currency },
                }],
                application_context: {
                  shipping_preference: 'NO_SHIPPING',
                  user_action: 'PAY_NOW',
                },
              });
            },
            onApprove(data, actions) {
              return actions.order.capture().then(function () {
                return postPay({ extra: 'Order pay' }, paymentLogSn).then(() => {
                  self.paypalDialogVisible = false;
                  self.dialogVisible = false;
                  self.refresh && self.refresh();
                  self.$router.replace('/paySuccess');
                });
              });
            },
            onCancel() {
              self.$message({ message: 'PayPal payment cancelled', type: 'warning' });
            },
            onError(err) {
              console.error('PayPal error:', err);
              self.$message({ message: 'PayPal payment failed', type: 'error' });
            },
          };

          window.paypal.Buttons({
            fundingSource: window.paypal.FUNDING.PAYPAL,
            style: { layout: 'horizontal', color: 'gold', shape: 'rect', label: 'paypal', tagline: false },
            ...buttonConfig,
          }).render('#order-paypal-button-container');

          try {
            const cardBtns = window.paypal.Buttons({
              fundingSource: window.paypal.FUNDING.CARD,
              style: { layout: 'horizontal', color: 'black', shape: 'rect', label: 'pay', tagline: false },
              ...buttonConfig,
            });
            if (cardBtns.isEligible()) {
              cardBtns.render('#order-paypal-card-button-container');
            }
          } catch (e) {
            console.warn('PayPal card button not eligible:', e);
          }
        });
      } catch (error) {
        console.error('PayPal payment error:', error);
        this.$message({ message: 'PayPal payment initialization failed', type: 'error' });
      }
    },
    onPaypalDialogClose() {
      const ppContainer = document.getElementById('order-paypal-button-container');
      const cardContainer = document.getElementById('order-paypal-card-button-container');
      if (ppContainer) ppContainer.innerHTML = '';
      if (cardContainer) cardContainer.innerHTML = '';
    },
    loadStripeJS() {
      return new Promise((resolve, reject) => {
        if (window.Stripe) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    resetStripeState() {
      this.stripeReady = false;
      this.stripeLoading = false;
      this.stripeStarted = false;
      this.stripeInstance = null;
      this.stripeElements = null;
      this.stripePaymentLogSn = null;
      const mount = document.getElementById('order-stripe-payment-element');
      if (mount) mount.innerHTML = '';
    },
    async startStripePayment() {
      if (this.stripeStarted && this.stripeReady) {
        return;
      }
      this.stripeStarted = true;
      this.stripeReady = false;
      this.stripeInstance = null;
      this.stripeElements = null;
      this.stripePaymentLogSn = null;
      try {
        await this.$nextTick();
        const mount = document.getElementById('order-stripe-payment-element');
        if (mount) mount.innerHTML = '';
        await this.preparePaymentContext();
        const response = await paymentIndex(this.paymentItemListForm, {
          paymentId: 'stripePayment',
          rePayUrl: 'payment',
        });
        this.paymentLog = response.data.data.paymentLog;
        const payRes = await paymentPay({
          extra: 'Order pay',
          paymentLogSn: this.paymentLog.sn,
        });
        const clientSecret = payRes.data.data.clientSecret;
        const publishableKey = payRes.data.data.publishableKey;
        this.stripePaymentLogSn = this.paymentLog.sn;

        await this.loadStripeJS();
        this.stripeInstance = window.Stripe(publishableKey);
        this.stripeElements = this.stripeInstance.elements({ clientSecret });
        const paymentElement = this.stripeElements.create('payment');
        paymentElement.mount('#order-stripe-payment-element');
        paymentElement.on('ready', () => {
          this.stripeReady = true;
        });
      } catch (error) {
        console.error('Stripe payment error:', error);
        this.$message({ message: 'Stripe payment initialization failed', type: 'error' });
        this.resetStripeState();
      }
    },
    handleStripePay() {
      if (!this.stripeInstance || !this.stripeElements) {
        return;
      }
      this.stripeLoading = true;
      const returnUrl = window.location.origin + '/#/paySuccess?paymentLogSn=' + this.stripePaymentLogSn;
      this.stripeInstance.confirmPayment({
        elements: this.stripeElements,
        confirmParams: {
          return_url: returnUrl,
        },
      }).then((result) => {
        if (result.error) {
          this.stripeLoading = false;
          this.$message({ message: result.error.message, type: 'error' });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.payment-dialog {
  .wrap {
    width: 450px;
    margin: 0 auto;

    .title {
      font-weight: 500;
      font-size: 16px;
      color: #333333;
      margin-bottom: 20px;
    }

    .action {
      padding: 30px 0;
      display: flex;
      align-items: center;
      justify-content: center;

      .el-button {
        width: 100%;
        height: 50px;
        color: #fff;
        background: var(--color-main);
        border-color: var(--color-main);
        border-radius: 4px;
      }
    }
  }
}
</style>
